// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullChampionship,
  $FullRegion,
  $FullWebRegatta,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getRegionByRegionSeasonId,
} from 'common.helpers/dist/browser';
import {
  getLocalItemBasePath,
} from '../../../../helpers';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import RegattasList from '../../../common/RegattasList';
import NoData from '../../../common/NoData';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  item: $FullChampionship;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$FullWebRegatta>;
  loading: boolean;
};

class RegattasSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getChampionshipRegattasSectionData({
        ChampionshipId: item.id,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.ChampionshipId === item.id && data.modelName === 'Regatta') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  render() {
    const {
      item,
      main,
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    const {
      RegionSeasonId,
    } = item;

    const region: $FullRegion = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId,
    });

    const shortDateFormat: string = region.ShortDateFormat;

    const basePath: string = getLocalItemBasePath({
      basePath: '/regatta',
      regions: main.regions,
      RegionSeasonId: item.RegionSeasonId,
    });

    return (
      <Section
        id={webSections[webPages.championshipPage].regattas}
        noMargin
        transparent
      >
        <Loader
          className={'overflow-x-auto'}
          fade
          loading={loading === true}
        >
          <div className={'bg-white rounded-lg p-5 mb-3'}>
            <h2 className={'h3'}>
              {t('Championship regattas')}
            </h2>
          </div>
          {data.length > 0 ? (
            <RegattasList
              basePath={basePath}
              data={data}
              shortDateFormat={shortDateFormat}
              showFullList
            />
          ) : (
            <NoData label={t('No regattas yet')} />
          )}
        </Loader>
      </Section>
    );
  }
}

export default RegattasSectionView;
